import { isEmpty } from 'lodash';
import {
  backdropClasses,
  Chip,
  ClickAwayListener,
  IconButton,
  Link,
  Popover,
  Stack,
  Theme,
  Typography,
} from '@common-components';
import { Close, Link as LinkIcon } from '@icons';
import { UserRole } from 'enums';
import { OptionsMenuState, useCurrentUser } from 'hooks';
import { messages } from 'i18n';
import { getEnvironment } from 'utils';
import { useAIContext } from 'broker/pages/SubmissionWorkspacePage/contexts/ai/AIContextProvider';
import { AIGeneratedContent } from './AIGeneratedContent';
import { AttachFilesButton } from './AttachFilesButton';
import { headerHeight } from './constants';
import { RegenerateButton, RegenerateButtonMode } from './RegenerateButton';
import { AIThreadInputDataButton } from './thread-input-data/AIThreadInputDataButton';

const openAIPlatformUrl = 'https://platform.openai.com/threads/';

interface AIPopupProps extends Omit<OptionsMenuState, 'setAnchorEl'> {
  popupTitle: string;
  withFileLinks?: boolean;
  isRichTextResponse?: boolean;
  popupWidth?: number;
  popupMinHeight?: number;
  popupMaxHeight?: string;
  hideRegenerateButton?: boolean;
  threadId?: string;
  anchorOrigin?: {
    vertical: 'top' | 'center' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  transformOrigin?: {
    vertical: 'top' | 'center' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  regenerateButtonMode?: RegenerateButtonMode;
  regenerateButtonHandler?: () => void;
  // set to false if you want to render the popup without the empty state even when there are no messages
  withEmptyState?: boolean;
  // allows to override the default behavior when the user enters a message
  addFollowupMessageCustomHandler?: (message: string) => Promise<void>;
  // handler for the attach files button, if not provided the attach files button will not be rendered
  attachFilesButtonHandler?: () => void;
  // custom count for selected files to display in attach files button
  selectedFilesCount?: number;
  // initial question to pre-fill in the input field
  initialQuestion?: string;
  // automatically submit the initial question when the popup opens
  autoRun?: boolean;
}
export default function AIPopup({
  popupTitle,
  withFileLinks = true,
  isRichTextResponse = true,
  anchorEl,
  isMenuOpen,
  closeMenu,
  popupMaxHeight,
  popupMinHeight,
  popupWidth,
  threadId,
  hideRegenerateButton = false,
  anchorOrigin = {
    vertical: 'top',
    horizontal: 'left',
  },
  transformOrigin = {
    vertical: 'top',
    horizontal: 'right',
  },
  regenerateButtonMode = RegenerateButtonMode.Regenerate,
  regenerateButtonHandler,
  withEmptyState = true,
  addFollowupMessageCustomHandler,
  attachFilesButtonHandler,
  selectedFilesCount,
  initialQuestion,
  autoRun,
}: AIPopupProps) {
  const { me } = useCurrentUser();
  const { initialMessages, messages: aiMessages } = useAIContext();
  const isInitThreadIdle = !isEmpty(initialMessages);
  const emptyMessages = isEmpty(aiMessages);
  const environment = getEnvironment();
  return (
    <ClickAwayListener onClickAway={closeMenu}>
      <Popover
        disableEnforceFocus
        disableScrollLock
        autoFocus={false}
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={closeMenu}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        PaperProps={{
          sx: {
            p: 1,
            width: popupWidth,
            minHeight: popupMinHeight,
            maxHeight: popupMaxHeight,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        sx={{
          zIndex: (theme: Theme) => theme.zIndex.tooltip,
          // 1. a hack to hide the backdrop and allow to scroll the content
          bottom: 'auto',
          // 2. a hack to hide the backdrop and allow to scroll the content
          [`& .${backdropClasses.root}`]: {
            display: 'none',
          },
        }}
      >
        <Stack
          direction="row"
          position="sticky"
          top={0}
          justifyContent="space-between"
          alignItems="center"
          height={headerHeight}
        >
          <Stack direction="row" gap={2}>
            <Typography ml={1} variant="h5">
              {popupTitle}
            </Typography>
            <AIThreadInputDataButton withFileLinks={withFileLinks} />
            {threadId && (environment !== 'prod' || me?.role === UserRole.BackOffice) && (
              <Chip
                variant="outlined"
                endIcon={LinkIcon}
                color="secondary"
                size="medium"
                tooltipContent={
                  <Link href={`${openAIPlatformUrl}${threadId}`} target="_blank" rel="noopener noreferrer">
                    {messages.submissionWorkspace.aiGeneratedContentPopup.openThreadInOpenAIPlatform}
                  </Link>
                }
                tooltipPlacement="bottom"
                disableFocusListener
              />
            )}
          </Stack>
          {!isInitThreadIdle && (
            <Stack direction="row" alignItems="center">
              {!hideRegenerateButton && !emptyMessages && (
                <RegenerateButton mode={regenerateButtonMode} regenerateButtonHandler={regenerateButtonHandler} />
              )}
              <IconButton icon={Close} onClick={closeMenu} variant="text" color="secondary" />
            </Stack>
          )}
        </Stack>
        <Stack flex={1} overflow="auto" textAlign="left">
          {attachFilesButtonHandler && (
            <AttachFilesButton onClick={attachFilesButtonHandler} selectedFilesCount={selectedFilesCount} />
          )}
          <AIGeneratedContent
            maxWidth={popupWidth}
            withFileLinks={withFileLinks}
            isRichTextResponse={isRichTextResponse}
            initialMessages={initialMessages}
            withEmptyState={withEmptyState}
            addFollowupMessageCustomHandler={addFollowupMessageCustomHandler}
            initialQuestion={initialQuestion}
            autoRun={autoRun}
          />
        </Stack>
      </Popover>
    </ClickAwayListener>
  );
}
